import { useEffect, useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { useLocation, useNavigate } from "react-router-dom";
import { encryptIdForUrl } from "@/utils/urlCrypto";
import {
  Search,
  Filter,
  Download,
  MoreHorizontal,
  Eye,
  UserPlus,
  MessageSquare,
  Archive,
  MapPin,
  ThumbsUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { KPICard } from "@/components/dashboard/KPICard";
import { SolicitacaoStatusBadge } from "@/components/solicitacoes/SolicitacaoStatusBadge";
import { SolicitacaoPrioridadeBadge } from "@/components/solicitacoes/SolicitacaoPrioridadeBadge";
import { useSolicitacoesStore } from "@/stores/solicitacoesStore";
import { useAgentesStore } from "@/stores/agentesStore";
import type { PrioridadeSolicitacao } from "@/types/solicitacao";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  STATUS_FILTRO_LABELS,
  type StatusSolicitacao,
  type EstadoAprovacaoSolicitacao,
} from "@/types/solicitacao";
import { format } from "date-fns";
import { SolicitacaoParcial } from "@/components/solicitacoes/core/models";
import { ptBR } from "date-fns/locale";
import { useTheme } from "@/hooks/use-theme";

// Normaliza: remove acentos, espaços, underscores e deixa lowercase.
// Necessário porque a API retorna valores com acentos/espaços (ex.: "Em análise").
const normalizeValue = (v?: string | null) =>
  (v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

export default function SolicitacoesPage() {
  const { theme } = useTheme();
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");

  const {
    solicitacoes,
    solicitacoesParciais,
    statsSolicitacoes,
    totalSolicitacoes,
    categorias,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    filtros,
    fetchSolicitacoes,
    fetchSolicitacoesParciais,
    fetchStatsSolicitacoes,
    fetchCategorias,
    refreshSolicitacoes,
    fetchMoreSolicitacoes,
    setFiltros,
    clearError,
  } = useSolicitacoesStore();
  // Paginação vem do store (server-side) via filtros.pagina/limite.
  const paginaAtual = filtros.pagina || 1;
  const itensPorPagina = filtros.limite || 20;
  const { isReadOnly } = useAgentePermissions();
  const { toast } = useToast();
  const { agentes, fetchAgentes } = useAgentesStore();

  const [isAtribuirDialogOpen, setIsAtribuirDialogOpen] = useState(false);
  const [isArquivarDialogOpen, setIsArquivarDialogOpen] = useState(false);
  const [selectedAgenteId, setSelectedAgenteId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCategorias();
    fetchStatsSolicitacoes();
    // Inicializa paginação server-side caso ainda não esteja definida.
    if (filtros.pagina === undefined || filtros.limite === undefined) {
      setFiltros({ pagina: 1, limite: 20 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shouldRefresh = (location.state as { shouldRefresh?: boolean } | null)?.shouldRefresh;
    if (shouldRefresh) {
      refreshSolicitacoes();
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega a lista
  useEffect(() => {
    const handleFocus = () => {
      refreshSolicitacoes();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshSolicitacoes();
      }
    };

    // Dispara um refresh inicial também
    refreshSolicitacoes();

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSolicitacoesParciais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.status, filtros.categoria_id, filtros.prioridade, filtros.tipo, filtros.busca, filtros.estadoAprovacao]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filtros.busca) {
        setFiltros({ busca: searchInput });
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, filtros.busca]);

  // Filtra solicitações localmente
  const solicitacoesFiltradas = solicitacoesParciais.filter((s) => {
    const estado = (s.estadoAprovacao || '').toString().toLowerCase();
    
    // Filtro por estado de aprovação
    const passaEstadoAprovacao = filtros.estadoAprovacao && filtros.estadoAprovacao !== 'all'
      ? true // A API já filtrou
      : estado !== 'reprovada'; // Por padrão, exclui as reprovadas
    
    // Filtro por busca na descrição do tópico ou UPN (protocolo)
    const termoBusca = searchInput.toLowerCase().trim();
    const passaBusca = !termoBusca 
      ? true 
      : (s.topico?.descricao || '').toLowerCase().includes(termoBusca) ||
        (s.upn || '').toLowerCase().includes(termoBusca);
    
    return passaEstadoAprovacao && passaBusca;
  });

  const toggleRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    setSelectedRows(
      selectedRows.length === paginatedSolicitacoes.length
        ? []
        : paginatedSolicitacoes.map((s) => s.id),
    );
  };

  const handleStatusChange = (value: string) => {
    // Converte o valor do filtro para o formato da API
    const statusMap: Record<string, string> = {
      'Pendente': 'pendente',
      'EmAnalise': 'EmAnalise',
      'Agendada': 'agendada',
      'Resolvida': 'resolvida',
      'AtribuidasAMim': 'AtribuidasAMim',
    };
    const mappedValue = value === 'all' ? 'all' : (statusMap[value] || value);
    setFiltros({ status: mappedValue as StatusSolicitacao | "all" });
  };

  const handleEstadoAprovacaoChange = (value: string) => {
    setFiltros({ estadoAprovacao: value as EstadoAprovacaoSolicitacao | "all" });
  };

  const handleRefresh = () => {
    refreshSolicitacoes();
  };

  const handleViewDetails = async (id: string) => {
    const encryptedId = await encryptIdForUrl(id);
    navigate(`/solicitacoes/${encryptedId}`);
  };

  // Calcula prioridade baseada no total de apoiadores:
// 0-10 = baixa, 11-30 = média, 31+ = alta
function calcularPrioridade(totalApoiadores: number): PrioridadeSolicitacao {
  if (totalApoiadores >= 31) return "alta";
  if (totalApoiadores >= 11) return "media";
  return "baixa";
}

const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  // Paginação client-side sobre a lista filtrada.
  const totalSolicitacoesFiltradas = solicitacoesFiltradas.length;
  const totalPages = Math.max(1, Math.ceil(totalSolicitacoesFiltradas / itensPorPagina));
  const currentPage = Math.min(paginaAtual, totalPages);
  const startIndex = (currentPage - 1) * itensPorPagina;
  const endIndex = Math.min(startIndex + itensPorPagina, totalSolicitacoesFiltradas);

  const paginatedSolicitacoes = solicitacoesFiltradas
    .sort((a, b) => new Date(b.dataCriacao).getTime() - new Date(a.dataCriacao).getTime())
    .slice(startIndex, endIndex);

  const showingFrom = totalSolicitacoesFiltradas > 0 ? startIndex + 1 : 0;
  const showingTo = endIndex;

  const handlePageChange = (page: number) => {
    const safePage = Math.max(1, Math.min(page, totalPages));
    setFiltros({ pagina: safePage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Se a busca ou filtros reduzirem o total de páginas, volta para a página 1.
  useEffect(() => {
    if (paginaAtual > totalPages) {
      setFiltros({ pagina: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, searchInput, filtros.status, filtros.estadoAprovacao]);

  const handleOpenAtribuir = () => {
    setSelectedAgenteId("");
    fetchAgentes("Aprovado", 1, true);
    setIsAtribuirDialogOpen(true);
  };

  const handleOpenArquivar = () => {
    setIsArquivarDialogOpen(true);
  };

  const handleAtribuirLote = async () => {
    if (!selectedAgenteId || selectedRows.length === 0) return;
    setIsSubmitting(true);
    try {
      await useSolicitacoesStore
        .getState()
        .atribuirAgenteLote(selectedRows, selectedAgenteId);
      toast({
        title: "Agente atribuído",
        description: `${selectedRows.length} solicitação(ões) atribuída(s).`,
      });
      setSelectedRows([]);
      setIsAtribuirDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Erro ao atribuir",
        description: error.message || "Erro ao atribuir agente",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArquivarLote = async () => {
    if (selectedRows.length === 0) return;
    setIsSubmitting(true);
    try {
      await useSolicitacoesStore
        .getState()
        .arquivarSolicitacaoLote(selectedRows, true);
      toast({
        title: "Solicitações arquivadas",
        description: `${selectedRows.length} solicitação(ões) arquivada(s).`,
      });
      setSelectedRows([]);
      setIsArquivarDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Erro ao arquivar",
        description: error.message || "Erro ao arquivar solicitações",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards — usam statsSolicitacoes (lista completa, SEM filtros aplicados)
          para que as contagens não mudem ao alterar os filtros da tabela.
          Pendente/Em Análise/Agendada → via status
          Reprovada/Aguardando Aprovação → via estadoAprovacao */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          title="Pendentes"
          value={
            statsSolicitacoes.filter(
              (s) =>
                normalizeValue(s.status) === normalizeValue("Pendente") &&
                normalizeValue(s.estadoAprovacao) === normalizeValue("Aprovada"),
            ).length
          }
          icon={Clock}
          color="#ef4444"
        />
        <KPICard
          title="Em Análise"
          value={
            statsSolicitacoes.filter(
              (s) =>
                normalizeValue(s.status) === normalizeValue("Em análise") &&
                normalizeValue(s.estadoAprovacao) === normalizeValue("Aprovada"),
            ).length
          }
          icon={CheckCircle}
          color="#f97316"
        />
        <KPICard
          title="Agendadas"
          value={
            statsSolicitacoes.filter(
              (s) =>
                normalizeValue(s.status) === normalizeValue("Agendada") &&
                normalizeValue(s.estadoAprovacao) === normalizeValue("Aprovada"),
            ).length
          }
          icon={CheckCircle}
          color="#CEDC38"
        />
        <KPICard
          title="Resolvidas"
          value={
            statsSolicitacoes.filter(
              (s) =>
                normalizeValue(s.status) === normalizeValue("Resolvida") &&
                normalizeValue(s.estadoAprovacao) === normalizeValue("Aprovada"),
            ).length
          }
          icon={CheckCircle}
          color="#22c55e"
        />
        <KPICard
          title="Aguardando Aprovação"
          value={
            statsSolicitacoes.filter(
              (s) =>
                normalizeValue(s.estadoAprovacao) ===
                normalizeValue("Aguardando Aprovação"),
            ).length
          }
          icon={Clock}
          color="#ef4444"
        />
        <KPICard
          title="Reprovadas"
          value={
            statsSolicitacoes.filter(
              (s) =>
                normalizeValue(s.estadoAprovacao) ===
                normalizeValue("Reprovada"),
            ).length
          }
          icon={XCircle}
          color={isDark ? "#ffffff" : "#000000"}
        />
      </div>

      {/* Filters Bar */}
      <div className="surface-elevated p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por tópico ou protocolo..."
              className="pl-10"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select
              value={filtros.status || "all"}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-full sm:w-[160px] flex-1 sm:flex-none min-w-0">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(STATUS_FILTRO_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filtros.estadoAprovacao || "all"}
              onValueChange={handleEstadoAprovacaoChange}
            >
              <SelectTrigger className="w-full sm:w-[200px] flex-1 sm:flex-none min-w-0">
                <SelectValue placeholder="Estado de Aprovação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Aprovada</SelectItem>
                <SelectItem value="AguardandoAprovacao">Aguardando aprovação</SelectItem>
                <SelectItem value="Reprovada">Reprovada</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearError}
            className="ml-auto"
          >
            Fechar
          </Button>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <span className="text-sm text-muted-foreground">
            {selectedRows.length > 0
              ? `${selectedRows.length} selecionada(s)`
              : `${totalSolicitacoesFiltradas} solicitações`}
          </span>
          {selectedRows.length > 0 && !isReadOnly && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleOpenAtribuir}>
                <UserPlus className="h-4 w-4 mr-2" />
                Atribuir
              </Button>
              <Button variant="outline" size="sm" onClick={handleOpenArquivar}>
                <Archive className="h-4 w-4 mr-2" />
                Arquivar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="surface-elevated overflow-hidden overflow-x-auto">
        {isLoading && paginatedSolicitacoes.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">
              Carregando solicitações...
            </span>
          </div>
        ) : paginatedSolicitacoes.length === 0 ? (
          <EmptyState
            title="Nenhuma solicitação encontrada"
            description="Ajuste os filtros ou aguarde novas solicitações"
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-surface-container hover:bg-surface-container">
                  <TableHead>
                    <Checkbox
                      checked={
                        selectedRows.length === paginatedSolicitacoes.length &&
                        paginatedSolicitacoes.length > 0
                      }
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead className="w-24 text-center">Foto</TableHead>
                  <TableHead className="w-[160px]">Protocolo</TableHead>
                  <TableHead>Solicitação</TableHead>
                  <TableHead className="w-[100px]">Tipo</TableHead>
                  <TableHead className="w-[140px]">Status</TableHead>
                  <TableHead className="w-[100px]">Prioridade</TableHead>
                  <TableHead className="w-[100px]">Data</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSolicitacoes.map((item: SolicitacaoParcial) => {
                    return (
                      <TableRow
                        key={item.id}
                        className="cursor-pointer hover:bg-surface-container/50"
                        onClick={() => {
                          handleViewDetails(item.id);
                          // Force refresh after navigation
                          setTimeout(() => {
                            refreshSolicitacoes();
                          }, 100);
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedRows.includes(item.id)}
                            onCheckedChange={() => toggleRow(item.id)}
                          />
                        </TableCell>
                        <TableCell className="text-center align-middle w-24">
                          {item.anexos?.[0]?.arquivo ? (
                            <img
                              src={item.anexos[0].arquivo}
                              alt="Foto da solicitação"
                              className="h-16 w-16 min-h-16 min-w-16 max-h-16 max-w-16 rounded-md object-cover border border-border mx-auto"
                              loading="lazy"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="h-16 w-16 min-h-16 min-w-16 max-h-16 max-w-16 rounded-md bg-muted flex items-center justify-center border border-border mx-auto">
                              <FileText className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-[10px] text-primary font-bold whitespace-nowrap">
                          {item.upn || 'N/A'}
                        </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm line-clamp-1">
                            {item.topico?.descricao || "Sem título"}
                          </p>
                          {item.endereco && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <MapPin className="h-3 w-3 text-muted-foreground" />
                              <span className="text-caption truncate max-w-[250px]">
                                {item.endereco.logradouro}, {item.endereco.numero}
                                {item.endereco.bairro &&
                                  `, ${item.endereco.bairro}`}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.topico?.tipo || "N/A"}
                      </TableCell>
                      <TableCell>
                        <SolicitacaoStatusBadge
                          status={item.status as StatusSolicitacao}
                          estadoAprovacao={item.estadoAprovacao}
                        />
                      </TableCell>
                      <TableCell>
                        <SolicitacaoPrioridadeBadge
                          prioridade={calcularPrioridade(item.totalApoiadores)}
                          showIcon={false}
                        />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(item.dataCriacao)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleViewDetails(item.id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Visualizar
                            </DropdownMenuItem>
                            {!isReadOnly && (
                              <>
                                <DropdownMenuItem>
                                  <UserPlus className="h-4 w-4 mr-2" />
                                  Atribuir Agente
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <MessageSquare className="h-4 w-4 mr-2" />
                                  Comentar
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Arquivar
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    );
                  })}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border">
              <div className="flex items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  Mostrando {showingFrom} a {showingTo} de {totalSolicitacoesFiltradas}{" "}
                  resultados
                </p>
                <Select
                  value={String(itensPorPagina)}
                  onValueChange={(value) => {
                    setFiltros({ limite: Number(value), pagina: 1 });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}/pág
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex"
                  disabled={currentPage === 1 || isLoading}
                  onClick={() => handlePageChange(1)}
                  title="Primeira página"
                >
                  {"<<"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1 || isLoading}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {totalPages > 0 && (
                  <>
                    {currentPage > 2 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-w-[40px]"
                          onClick={() => handlePageChange(1)}
                        >
                          1
                        </Button>
                        {currentPage > 3 && <span className="text-muted-foreground">...</span>}
                      </>
                    )}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const offset = Math.max(0, Math.min(currentPage - 3, totalPages - 5));
                      const pageNum = offset + i + 1;
                      if (pageNum < 1 || pageNum > totalPages) return null;
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className="min-w-[40px]"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    {currentPage < totalPages - 1 && totalPages > 5 && (
                      <>
                        {currentPage < totalPages - 2 && <span className="text-muted-foreground">...</span>}
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-w-[40px]"
                          onClick={() => handlePageChange(totalPages)}
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages || isLoading}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex"
                  disabled={currentPage >= totalPages || isLoading}
                  onClick={() => handlePageChange(totalPages)}
                  title="Última página"
                >
                  {">>"}
                </Button>
                <span className="text-sm text-muted-foreground ml-1">
                  Pág {currentPage} de {totalPages}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dialog Atribuir Agente em Lote */}
      <Dialog open={isAtribuirDialogOpen} onOpenChange={setIsAtribuirDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atribuir agente</DialogTitle>
            <DialogDescription>
              Escolha o agente para atribuir às {selectedRows.length} solicitação(ões) selecionada(s).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedAgenteId} onValueChange={setSelectedAgenteId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um agente" />
              </SelectTrigger>
              <SelectContent>
                {agentes.map((agente) => (
                  <SelectItem key={agente.id} value={agente.id}>
                    {agente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAtribuirDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAtribuirLote}
              disabled={!selectedAgenteId || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Atribuir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Arquivar em Lote */}
      <Dialog open={isArquivarDialogOpen} onOpenChange={setIsArquivarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arquivar solicitações</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja arquivar {selectedRows.length} solicitação(ões) selecionada(s)?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsArquivarDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleArquivarLote}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Arquivar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
